import { PropsWithChildren, useMemo } from "react";

import { newGuid } from "@bps/common-utils";
import { CenteredBox, IDialogProps } from "@bps/fluent-ui";
import { ErrorAlert } from "@components/errors/ErrorAlert";

import { JobsConfigurationFormValues, JobsConfigurationJob } from "./dtos";
import { JobsConfiguration } from "./JobsConfiguration";

interface JobsConfigurationJsonWrapperProps
  extends Pick<IDialogProps, "onDismiss"> {
  value: string;
  onUpdate: (value: string) => void;
}

export const JobsConfigurationJsonWrapper: React.FC<PropsWithChildren<
  JobsConfigurationJsonWrapperProps
>> = ({ value, onUpdate, onDismiss, ...props }) => {
  const save = async (values: JobsConfigurationFormValues) => {
    // Strip out arbitrary IDs
    const jobsWithoutIds = values.jobs.map(job => {
      const { id, commands, ...rest } = job;
      return {
        ...rest,
        commands: commands.map(command => {
          const { id, ...rest } = command;
          return rest;
        })
      };
    });

    onUpdate(JSON.stringify(jobsWithoutIds));
    onDismiss?.();
  };

  const initialValues = useMemo(() => jobsJsonToFormValues(value), [value]);
  if (!initialValues) {
    return (
      <CenteredBox>
        <ErrorAlert error="Job configuration JSON was invalid. You may need to reset this field to the default value." />
      </CenteredBox>
    );
  }

  return (
    <JobsConfiguration
      initialValues={initialValues}
      onUpdate={save}
      onDismiss={onDismiss}
      {...props}
    />
  );
};

function jobsJsonToFormValues(json: string) {
  let jobs: JobsConfigurationJob[] = [];
  try {
    jobs = JSON.parse(json);
  } catch {
    return null;
  }

  return {
    jobs: jobs
      .map(job => {
        const { commands, ...rest } = job;
        return {
          ...rest,
          commands: commands.map(command => {
            return {
              ...command,
              id: newGuid()
            };
          }),
          id: newGuid()
        };
      })
      ?.sort((a, b) =>
        !!a.code
          ? a.code.localeCompare(b.code)
          : a.name.localeCompare(b.name) ?? -1
      )
  };
}
